import type { RynReplayEventProperties } from '@ups/xplat/base/ryn-replay';
import {
  extractVisibleKendoGridColumns,
  slimRowByColumns,
  type IRynReplayGridColumnDef,
  type IRynReplayKendoGridLike,
} from './ryn-replay-grid-columns';
import {
  buildRynReplayGridLeaveProperties,
  buildRynReplayGridSnapshot,
  type RynReplayGridSnapshotCell,
  type RynReplayGridSnapshotMeta,
} from './ryn-replay-grid-snapshot';
import {
  IRynReplayGridSnapshotPageConfig,
  IRynReplayGridSnapshotsConfig,
  leaveEventNameForPage,
  routeIncludesList,
  snapshotEventNameForPage,
} from './ryn-replay-grid-snapshots.config';

/** Minimal recorder surface so pages do not import the full service type. */
export interface IRynReplayGridSnapshotTracker {
  track(name: string, properties?: RynReplayEventProperties): void;
}

export interface IRynReplayGridPageTrackInput<TRow> {
  reason: string;
  meta?: RynReplayGridSnapshotMeta;
  rows: TRow[] | null | undefined;
  /** Live Kendo grid — visible columns win when present. */
  grid?: IRynReplayKendoGridLike | null;
  /** Explicit columns (tests / overrides). */
  columns?: readonly IRynReplayGridColumnDef[];
  /**
   * Page-local fallback when neither `columns` nor live grid columns exist.
   * Provider `fallbackColumns` is used if this is omitted.
   */
  fallbackColumns?: readonly IRynReplayGridColumnDef[];
  includeRow?: (row: TRow) => boolean;
  getCellValue?: (row: unknown, key: string) => unknown;
  slimRow?: (row: TRow) => Record<string, RynReplayGridSnapshotCell>;
  take?: number | null;
  skip?: number | null;
}

export function findRynReplayGridSnapshotPage(
  config: IRynReplayGridSnapshotsConfig | null | undefined,
  pageId: string
): IRynReplayGridSnapshotPageConfig | undefined {
  return config?.pages?.find((page) => page.id === pageId);
}

/**
 * Emit `{page.id}:snapshot` using the app-module page config for title,
 * route needles, and optional fallback columns. Pages should call this instead
 * of a hand-rolled `track('…:snapshot', …)` wrapper.
 */
export function trackRynReplayGridPageSnapshot<TRow>(
  recorder: IRynReplayGridSnapshotTracker,
  page: IRynReplayGridSnapshotPageConfig,
  input: IRynReplayGridPageTrackInput<TRow>
): void {
  const fromGrid = extractVisibleKendoGridColumns(input.grid);
  const columns = input.columns?.length
    ? input.columns
    : fromGrid.length
      ? fromGrid
      : (input.fallbackColumns ?? page.fallbackColumns ?? []);

  const slimRow =
    input.slimRow ??
    ((row: TRow) => slimRowByColumns(row, columns, input.getCellValue));

  recorder.track(
    snapshotEventNameForPage(page.id),
    buildRynReplayGridSnapshot({
      reason: input.reason,
      pageId: page.id,
      title: page.title,
      urlIncludes: routeIncludesList(page.routeIncludes),
      meta: input.meta,
      columns,
      rows: input.rows,
      includeRow: input.includeRow,
      slimRow,
      take: input.take,
      skip: input.skip,
      maxRows: page.maxRows,
      unmaskedColumns: page.unmaskedColumns,
    })
  );
}

/** Emit `{page.id}:left` so the player hides the table after navigation. */
export function leaveRynReplayGridPage(
  recorder: IRynReplayGridSnapshotTracker,
  page: IRynReplayGridSnapshotPageConfig
): void {
  recorder.track(
    leaveEventNameForPage(page.id),
    buildRynReplayGridLeaveProperties({ pageId: page.id })
  );
}
